(ns ui.view.entity-graph.components.modal
  (:require [re-frame.core :as rf]
            [clojure.string :as string]
            [common.localization :as loc]
            [common.entity :as entity]
            [common.schema.entities.graph-reference :as graph-reference]
            [common.schema.entities.graph-entity :as graph-entity]
            [common.schema.entity-types :as entity-types]
            [common.schema.field-spec :as field-spec]
            [common.schema.registry :as registry]
            [frontend.state.permissions :as permissions]
            [frontend.utils :as fu]
            [frontend.view.entity :as f-entity]
            [ui.util :as uu]
            [ui.state.session.sub :as session-sub]
            [ui.view.entity-graph.model :as m]
            [ui.view.entity-graph.utils :as utils]
            [ui.editors.labelled-input.view :as labelled-input]
            [ui.components.modal.view :as modal]
            [ui.components.button.view :as button]))

(defn delete-confirmation-modal
  [ctx]
  (let [{:keys [open?
                connector-id
                connector-type
                source-id
                target-id]} @(rf/subscribe [m/delete-modal ctx])
        close! (fn [_]
                 (rf/dispatch [m/cancel-delete ctx]))
        source @(rf/subscribe [::session-sub/entity source-id])
        target @(rf/subscribe [::session-sub/entity target-id])]
    [modal/view
     {:visible? open?
      :hide! close!}
     [:div (loc/tr :delete-graph-ref-title [(loc/tr connector-type)])]
     [:div
      (loc/tr :delete-graph-ref-info [(:graph-entity/label source)
                                  (:graph-entity/label target)])]
     [:div
      {:class-name "flex w-full justify-between"}
      [button/sm-secondary-hollow
       {:on-click
        close!}
       (loc/tr :cancel)]
      [button/sm-danger-solid
       {:on-click
        (fn [_]
          (rf/dispatch [m/confirm-delete (assoc ctx
                                                :entity-id connector-id)]))}
       (loc/tr :delete)]]]))

(def create-graph-ref-titles
  {:graph-reference.type/ownership
   {:source :adding-ownership
    :target :adding-an-owner}
   :graph-reference.type/gp
   {:source :set-as-gp-of
    :target :add-gp-for}
   :graph-reference.type/lp
   {:source :set-as-lp-in
    :target :add-lp-for}
   :graph-reference.type/board-member
   {:source :set-as-board-member-of
    :target :add-board-member-for}
   :graph-reference.type/executive
   {:source :set-as-executive-of
    :target :add-executive-for}
   :graph-reference.type/auditor
   {:source :set-as-auditor-for
    :target :add-auditor-for}
   :graph-reference.type/auditing-firm
   {:source :set-as-auditing-firm-in
    :target :add-auditing-firm-for}
   :graph-reference.type/management-company
   {:source :set-as-management-company-of
    :target :add-management-company-for}
   :graph-reference.type/aifm
   {:source :set-as-aifm-of
    :target :add-aifm-for}
   :graph-reference.type/advisor
   {:source :set-as-advisor-of
    :target :add-advisor-for}})

(def selector-labels
  {:graph-reference.type/ownership
   {:source :add-ownership-in
    :target :selector-label-owner}
   :graph-reference.type/gp
   {:source :selector-label-fund
    :target :selector-label-gp}
   :graph-reference.type/lp
   {:source :selector-label-fund
    :target :selector-label-lp}
   :graph-reference.type/board-member
   {:source :selector-label-company
    :target :selector-label-board-member}
   :graph-reference.type/executive
   {:source :selector-label-company
    :target :selector-label-executive}
   :graph-reference.type/auditor
   {:source :selector-label-company
    :target :selector-label-auditor}
   :graph-reference.type/auditing-firm
   {:source :selector-label-company
    :target :selector-label-auditing-firm}})

(def override-editor-types
  {:graph-entity/entity :editor.type/text-simple})

(def override-data-types
  {:graph-entity/entity :data.type/text})

(defn clamp [min-val max-val val]
  (when val
    (max (min max-val val) min-val)))

(defn map-profile-attributes
  [_ entity-type]
  (let [tenant-schema @(rf/subscribe [::session-sub/tenant-schema])
        schema (registry/get-schema tenant-schema entity-type)
        visible-schema (->> schema
                            (filterv (fn [[attribute _fs]]
                                       (let [read? (permissions/field-read-access?
                                                    attribute entity-type nil)]
                                         read?)))
                            (filterv (fn [[a _]]
                                       (= (namespace a)
                                          "graph-entity")))
                            (sort-by (fn [[a _]]
                                       (utils/attr-order a))))]
    visible-schema))

(defn map-profile-changes?
  [map-profile edits]
  (->> edits
       (some (fn [[k v]]
               (not= (get map-profile k)
                     v)))))

(defn map-profile-fields
  [ctx selected-id]
  (let [selected-entity @(rf/subscribe [::session-sub/entity selected-id])
        entity-type (:entity/type selected-entity)
        map-profile? (contains? entity-types/graph-entity-types entity-type)
        map-profile (when map-profile?
                      @(rf/subscribe [::session-sub/entity selected-id]))
        target-entity (if map-profile?
                        @(rf/subscribe [::session-sub/entity (:graph-entity/entity
                                                              map-profile)])
                        selected-entity)
        map-entity-type (if map-profile?
                          (:entity/type map-profile)
                          (get graph-entity/entity-graph-mapping entity-type))
        edits @(rf/subscribe [m/map-profile-edits ctx])]
    [:div
     {:class "flex flex-col gap-3"}
     (doall
      (for [[attribute {:field/keys [default-visibility
                                     data-type]
                        :keys [named-value-attribute] :as fs}]
            (map-profile-attributes ctx
                                    map-entity-type)
            :let [value (if (contains? edits attribute)
                          (get edits attribute)
                          (get map-profile attribute))
                  editor-type (or (get override-editor-types attribute)
                                  (:editor default-visibility))
                  data-type (or (get override-data-types attribute)
                                data-type)
                  value (if (= attribute :graph-entity/entity)
                          (entity/display-string target-entity)
                          value)
                  percentage? (= data-type :data.type/percentage)
                  value (if percentage?
                          (clamp 0 1 value)
                          value)
                  mandatory? (field-spec/system-required-or-legacy-always-mandatory-field?
                              fs)
                  not-clearable? (field-spec/not-clearable-after-set-field? fs)
                  disabled? (= attribute :graph-entity/entity)]
            :when (and (not= editor-type :editor.type/none)
                       (not= attribute :graph-entity/entity))]
        ^{:key (str "m" selected-id "-" attribute)}
        [:div
         {:class "flex flex-col gap-3"}
         [:div
          {:class (uu/cm (when (= editor-type :editor.type/toggle)
                           "max-w-[40px] *:!flex-row"))}
          [labelled-input/view
           {:label (loc/tr attribute)
            :value value
            :is-clearable? (and (not not-clearable?)
                                (not mandatory?))
            :disabled? disabled?
            :on-change (fn [new-value]
                         (let [new-value (if percentage?
                                           (clamp 0 1 new-value)
                                           new-value)]
                           (rf/dispatch-sync
                            [m/edit-map-profile
                             (assoc ctx
                                    :attribute attribute
                                    :value new-value)])))
            :options (when named-value-attribute
                       (->> (fu/named-value-options fs nil)
                            (sort-by :common/ord)
                            (mapv (fn [{:keys [db/id
                                               named-value/value]}]
                                    {:value id
                                     :label value}))))
            :data-type data-type
            :editor-type editor-type
            :create! (when named-value-attribute
                       (fn [query]
                         (f-entity/open-creator!
                          (:field/entity-types fs)
                          {:named-value-attribute named-value-attribute
                           :start-value query
                           :attribute attribute
                           :skip-success-msg? true
                           :creation-pipeline-id nil
                           ;;:dispatch-on-create-nev-2024 [::event/create-option-success ctx cell-data]
                           :on-ok! (fn [{:keys [new-entity-id close-cb!] :as _args}]
                                     (rf/dispatch-sync
                                      [m/edit-map-profile
                                       (assoc ctx
                                              :attribute attribute
                                              :value new-entity-id)])
                                     (close-cb!))})))}]]]))]))

(defn map-profile-section
  [ctx map-profile-id]
  [:div
   {:class (uu/cm "mt-3 p-5 pt-4 bg-complement-light rounded-md")}
   [:div
    {:class (uu/cm "text-lg pb-3 font-bold")}
    (loc/tr :map-information)]
   [map-profile-fields ctx map-profile-id]])

(defn map-profile-selector-input
  [ctx {:keys [edits direction map-profile]}]
  (let [{:keys [selected]} edits
        map-profile-id (:db/id map-profile)
        connector-type :graph-reference.type/ownership
        entity-types (get-in graph-reference/allowed-entity-types
                             [connector-type
                              (if (= direction :source)
                                :target
                                :source)])
        placeholder (if (= direction :source)
                      (loc/tr :placeholder-select-target)
                      (loc/tr :placeholder-select-owner))
        other-ids #{map-profile-id (:graph-entity/entity map-profile)}
        options (->> @(rf/subscribe [m/connector-options
                                     {:entity-types entity-types
                                      :other-ids other-ids}]))]
    [labelled-input/view
     {:editor-type :editor.type/select
      :data-type :data.type/ref-single
      :label nil;;selector-label
      :placeholder placeholder
      :class (uu/cm "placeholder-current")
      :value selected
      :options options
      :on-change (fn [id _opt]
                   (when-not (= selected id)
                     (rf/dispatch-sync
                      [m/start-edit-map-profile
                       (assoc ctx
                              :target-entity-id id)])
                     (rf/dispatch-sync
                      [m/update-create-connector
                       (assoc ctx
                              :attribute :selected
                              :value id)])))
      :create! (fn [query]
                 (f-entity/open-creator!
                  entity-types
                  {:start-value query
                   :skip-success-msg? true
                   :creation-pipeline-id nil
                   :on-ok! (fn [{:keys [new-entity-id close-cb!] :as _args}]
                             (when-not (= selected new-entity-id)
                               (rf/dispatch-sync
                                [m/start-edit-map-profile
                                 (assoc ctx
                                        :target-entity-id new-entity-id)])
                               (rf/dispatch-sync
                                [m/update-create-connector
                                 (assoc ctx
                                        :attribute :selected
                                        :value new-entity-id)]))
                             (close-cb!))}))}]))

(defn percentage-input
  [ctx edits]
  (let [attribute :graph-meta/ownership-percentage
        {{:keys [editor]} :field/default-visibility
         :field/keys [data-type] :as _fs} (registry/get-field-schema
                                           nil attribute)
        value (get edits attribute)
        value (clamp 0 1 value)]
    [:div
     [labelled-input/view
      {:data-type data-type
       :label nil
       :placeholder (loc/tr :placeholder-percentage-example)
       :editor-type editor
       :value value
       :on-change (fn [new-value]
                    (let [new-value (clamp 0 1 new-value)]
                      (rf/dispatch-sync
                       [m/update-create-connector
                        (assoc ctx
                               :attribute attribute
                               :value new-value)])))}]]))

(defn ownership-component
  [ctx]
  (let [{:keys [direction
                map-profile-id edits]} @(rf/subscribe [m/create-connector-modal ctx])
        map-profile @(rf/subscribe [::session-sub/entity map-profile-id])
        map-profile-label (:graph-entity/label map-profile)
        info-kw (if (= direction :source)
                  :add-ownership-info
                  :add-an-owner-info)
        info (loc/tr info-kw [map-profile-label])]
    [:div
     {:class "flex flex-col"}
     [:div info]
     (if (= direction :source)
       [:div
        {:class (uu/cm "pt-3 flex gap-2")}
        [:div
         {:class (uu/cm "flex-none w-24")}
         [percentage-input ctx edits]]
        [:div
         {:class (uu/cm "align-middle flex items-center")}
         (loc/tr :in)]
        [:div
         {:class (uu/cm "flex-1")}
         [map-profile-selector-input
          ctx {:edits edits :direction direction
               :map-profile map-profile}]]]
       [:div
        {:class (uu/cm "pt-3 flex gap-2")}
        [:div
         {:class (uu/cm "grow-1")}
         [map-profile-selector-input
          ctx {:edits edits :direction direction
               :map-profile map-profile}]]
        [:div
         {:class (uu/cm "align-middle flex items-center grow-1")}
         (loc/tr :ownership-in [map-profile-label])]
        [:div
         {:class (uu/cm "flex-none w-24")}
         [percentage-input ctx edits]]])]))

(defn create-connector-modal
  [ctx]
  (let [{:keys [open?
                connector-type
                direction
                map-profile-id edits]} @(rf/subscribe [m/create-connector-modal ctx])
        ref-type connector-type
        close! (fn [_]
                 (rf/dispatch [m/close-create-connector ctx]))
        map-profile @(rf/subscribe [::session-sub/entity map-profile-id])
        map-profile-label (:graph-entity/label map-profile)

        entity-types (get-in graph-reference/allowed-entity-types
                             [connector-type
                              (if (= direction :source)
                                :target
                                :source)])
        other-ids #{map-profile-id (:graph-entity/entity map-profile)}
        options (->> @(rf/subscribe [m/connector-options
                                     {:entity-types entity-types
                                      :other-ids other-ids}]))
        {:keys [selected]} edits
        validations @(rf/subscribe [m/validate-create-connector-modal
                                    ctx])
        disabled? (seq validations)
        title-kw (get-in create-graph-ref-titles [connector-type direction])
        title (loc/tr title-kw [map-profile-label])
        selector-label (loc/tr (get-in selector-labels [connector-type direction] :select))
        metadatas (get graph-reference/graph-references-metadata ref-type)]
    [modal/view
     {:visible? open?
      :hide! close!}
     [:div
      title]
     [:div
      {:class "flex flex-col gap-4"}
      (case connector-type
        ;; more specific one for ownership
        :graph-reference.type/ownership
        [ownership-component ctx]
        [:<>
         (doall
          (for [attribute metadatas
                :let [{{:keys [editor]} :field/default-visibility
                       :field/keys [data-type]
                       :keys [options] :as _fs} (registry/get-field-schema
                                                 nil attribute)
                      percentage? (= data-type :data.type/percentage)
                      value (get edits attribute)
                      value (if percentage?
                              (clamp 0 1 value)
                              value)]]
            ^{:key (str attribute)}
            [:div
             [labelled-input/view
              {:data-type data-type
               :label (loc/tr attribute)
               :editor-type editor
               :options (->> options)
               :value value
               :on-change (fn [new-value]
                            (let [new-value (if percentage?
                                              (clamp 0 1 new-value)
                                              new-value)]
                              (rf/dispatch-sync
                               [m/update-create-connector
                                (assoc ctx
                                       :attribute attribute
                                       :value new-value)])))}]]))
         [labelled-input/view
          {:editor-type :editor.type/select
           :data-type :data.type/ref-single
           :label selector-label
           :value selected
           :options options
           :on-change (fn [id _opt]
                        (when-not (= selected id)
                          (rf/dispatch-sync
                           [m/start-edit-map-profile
                            (assoc ctx
                                   :target-entity-id id)])
                          (rf/dispatch-sync
                           [m/update-create-connector
                            (assoc ctx
                                   :attribute :selected
                                   :value id)])))
           :create! (fn [query]
                      (f-entity/open-creator!
                       entity-types
                       {:start-value query
                        :skip-success-msg? true
                        :creation-pipeline-id nil
                        :on-ok! (fn [{:keys [new-entity-id close-cb!] :as _args}]
                                  (when-not (= selected new-entity-id)
                                    (rf/dispatch-sync
                                     [m/start-edit-map-profile
                                      (assoc ctx
                                             :target-entity-id new-entity-id)])
                                    (rf/dispatch-sync
                                     [m/update-create-connector
                                      (assoc ctx
                                             :attribute :selected
                                             :value new-entity-id)]))
                                  (close-cb!))}))}]])
      (when selected
        ;; force rerender when changing map-profile:
        ^{:key (str "map-profile-section-" selected)}
        [map-profile-section ctx selected])]
     [:div
      {:class "w-full flex gap-3 justify-between"}
      [button/secondary-hollow
       {:on-click
        close!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:disabled? disabled?
        :tooltip (when (seq validations)
                   (->> validations
                        (mapv second)
                        (filterv string?)
                        (string/join ", ")))
        :on-click
        (fn [_]
          (rf/dispatch [m/create-connector
                        (assoc ctx
                               :selected selected
                               :source-id (if (= direction :source)
                                            map-profile-id
                                            selected)
                               :target-id (if (= direction :source)
                                            selected
                                            map-profile-id)
                               :type connector-type)]))}
       (loc/tr :save)]]]))

(defn edit-map-profile-modal
  [ctx]
  (let [{:keys [open?
                map-profile-id]} @(rf/subscribe [m/edit-map-profile-modal ctx])
        close! (fn [_]
                 (rf/dispatch [m/discard-map-profile ctx]))
        map-profile @(rf/subscribe [::session-sub/entity map-profile-id])
        mp-entity @(rf/subscribe [::session-sub/entity (:graph-entity/entity
                                                        map-profile)])
        edits @(rf/subscribe [m/map-profile-edits ctx])
        changes? (map-profile-changes? map-profile edits)
        label (:graph-entity/label map-profile)]
    [modal/view
     {:visible? open?
      :hide! close!}
     [:div "Edit " label]
     [:div
      {:class "flex flex-col gap-3"}
      (doall
       (for [[attribute {:field/keys [default-visibility
                                      data-type]
                         :keys [named-value-attribute] :as fs}]
             (map-profile-attributes ctx
                                     (:entity/type map-profile))
             :let [value (if (contains? edits attribute)
                           (get edits attribute)
                           (get map-profile attribute))
                   editor-type (or (get override-editor-types attribute)
                                   (:editor default-visibility))
                   data-type (or (get override-data-types attribute)
                                 data-type)
                   value (if (= attribute :graph-entity/entity)
                           (entity/display-string mp-entity)
                           value)
                   mandatory? (field-spec/system-required-or-legacy-always-mandatory-field?
                               fs)
                   not-clearable? (field-spec/not-clearable-after-set-field? fs)
                   disabled? (= attribute :graph-entity/entity)]
             :when (not= editor-type :editor.type/none)]
         ^{:key (str attribute)}
         [:div
          {:class "flex flex-col gap-3"}
          [:div
           {:class (uu/cm (when (= editor-type :editor.type/toggle)
                            "max-w-[40px]"))}
           [labelled-input/view
            {:label (loc/tr attribute)
             :value value
             :is-clearable? (and (not not-clearable?)
                                 (not mandatory?))
             :disabled? disabled?
             :on-change (fn [new-value]
                          (rf/dispatch-sync
                           [m/edit-map-profile
                            (assoc ctx
                                   :attribute attribute
                                   :value new-value)]))
             :options (when named-value-attribute
                        (->> (fu/named-value-options fs nil)
                             (sort-by :common/ord)
                             (mapv (fn [{:keys [db/id
                                                named-value/value]}]
                                     {:value id
                                      :label value}))))
             :data-type data-type
             :editor-type editor-type
             :create! (when named-value-attribute
                        (fn [query]
                          (f-entity/open-creator!
                           (:field/entity-types fs)
                           {:named-value-attribute named-value-attribute
                            :start-value query
                            :attribute attribute
                            :skip-success-msg? true
                            :creation-pipeline-id nil
                                 ;;:dispatch-on-create-nev-2024 [::event/create-option-success ctx cell-data]
                            :on-ok! (fn [{:keys [new-entity-id close-cb!] :as _args}]
                                      (rf/dispatch-sync
                                       [m/edit-map-profile
                                        (assoc ctx
                                               :attribute attribute
                                               :value new-entity-id)])
                                      (close-cb!))})))}]]]))]
     [:div
      {:class-name "flex w-full justify-between gap-3"}
      [button/secondary-hollow
       {:on-click
        close!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:disabled? (not changes?)
        :on-click
        (fn [_]
          (rf/dispatch [m/save-map-profile (assoc ctx
                                                  :map-profile-id map-profile-id)]))}
       (loc/tr :save)]]]))

(def edit-reference-note
  {:graph-reference.type/ownership
   {:graph-meta/ownership-percentage
    :edit-ref-note/ownership-percentage}
   :graph-reference.type/board-member
   {:graph-meta/board-member-role
    :edit-ref-note/board-member-role}
   :graph-reference.type/executive
   {:graph-meta/executive-role
    :edit-ref-note/executive-role}
   :graph-reference.type/auditor
   {:graph-meta/auditor-role
    :edit-ref-note/auditor-role}})

(defn edit-graph-reference-modal
  [ctx]
  (let [{:keys [open?
                connector-id
                other-mp-id
                edits]} @(rf/subscribe [m/edit-connector-modal ctx])
        close! (fn [_]
                 (rf/dispatch-sync [m/discard-connector ctx]))
        connector @(rf/subscribe [::session-sub/entity connector-id])
        source-id (:graph-reference/source connector)
        target-id (:graph-reference/target connector)

        source @(rf/subscribe [::session-sub/entity source-id])
        source-label (:graph-entity/label source)
        target @(rf/subscribe [::session-sub/entity target-id])
        target-label (:graph-entity/label target)

        ref-type (:graph-reference/type connector)
        title (loc/tr :edit-relationship)
        metadatas (get graph-reference/graph-references-metadata ref-type)
        validations @(rf/subscribe [m/validate-edit-graph-ref-modal
                                    ctx])
        disabled? (seq validations)
        save-button-tooltip (when (seq validations)
                              (->> validations
                                   (mapv second)
                                   (filterv string?)
                                   (string/join ", ")))]
    [modal/view
     {:visible? open?
      :hide! close!}
     [:div
      title]
     [:div
      {:class "flex flex-col gap-4"}
      [:div
       {:class "flex flex-col"}
       (doall
        (for [attribute metadatas
              :let [{{:keys [editor]} :field/default-visibility
                     :field/keys [data-type]
                     :keys [options] :as _fs} (registry/get-field-schema
                                               nil attribute)
                    value (if (contains? edits attribute)
                            (get edits attribute)
                            (utils/metadata-value connector attribute))
                    percentage? (= data-type :data.type/percentage)
                    value (if percentage?
                            (clamp 0 1 value)
                            value)]]
          ^{:key (str attribute)}
          [:<>
           [:div
            {:class "m-0 font-semibold text-sm"}
            (loc/tr (get-in edit-reference-note
                        [ref-type attribute])
                [source-label target-label])]
           [:div
            [labelled-input/view
             {:data-type data-type
              :editor-type editor
              :value value
              :options options
              :on-change (fn [new-value]
                           (let [new-value (if percentage?
                                             (clamp 0 1 new-value)
                                             new-value)]
                             (rf/dispatch-sync
                              [m/edit-connector
                               (assoc ctx
                                      :attribute attribute
                                      :value new-value)])))}]]]))]
      (when other-mp-id
        [map-profile-section ctx other-mp-id])]
     [:div
      {:class-name "w-full flex justify-between"}
      [button/secondary-hollow
       {:on-click
        close!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:disabled? disabled?
        :tooltip save-button-tooltip
        :on-click
        (fn [_]
          (rf/dispatch [m/save-connector
                        (assoc ctx
                               :connector-id connector-id)])
          (when other-mp-id
            (rf/dispatch [m/save-map-profile
                          (assoc ctx :map-profile-id other-mp-id)])))}
       (loc/tr :save)]]]))

(defn modals
  [ctx]
  (let [edit-ref? (:open? @(rf/subscribe [m/edit-connector-modal ctx]))]
    [:<>
     [delete-confirmation-modal ctx]
     [create-connector-modal ctx]
     [edit-map-profile-modal ctx]
     (when edit-ref?
       [edit-graph-reference-modal ctx])]))
